import React, { useState, useEffect } from 'react';
import apiClient from '../../../apiClient';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiPlus, FiTrash } from 'react-icons/fi';
import Header from '../../common/Header/Header';
import Button from '../../common/Button/Button';
import './AddEnquiryForm.css';

const AddEnquiryForm = ({
  showAddForm,
  handleToggleAddForm,
  closePopup,
  isLoading,
  handleAddNewEnquiry,
  newEnquiryFormData,
  handleNewEnquiryChange,
  showContactDropdown,
  filteredContacts,
  handleContactSelect,
  contactSearch,
  setShowContactFormCallback,
  setShowContactDropdown,
  showCompanyDropdown,
  filteredCompanies,
  handleCompanySelect,
  showProductDropdown,
  filteredProducts,
  handleProductSelect,
  handleContactNameChange,
  handleCompanyNameChange,
  handleProductNameChange,
  setShowCompanyDropdown,
  companySearch,
  setShowProductDropdown,
  productSearch,
  setShowContactForm,
  setShowCompanyForm,
  setShowNewProductForm,
  newContactData,
  setNewContactData,
  handleAddNewContact,
  newProductData,
  setNewProductData,
  newCompanyData,
  setNewCompanyData,
  handleAddNewCompany
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    enquiryID: "",
    date: new Date().toISOString().split('T')[0],
    buyerName: "",
    description: "",
    quotationFor: "",
    productType: "",
    emailID: "",
    phoneNumber: "",
    city: "",
    state: "",
    status: "Pending",
    cat_Id: "",
    note: "",
    attributes: [{ name: "", value: "" }],
    attachment: null,
  });

  const [categories, setCategories] = useState([]);
  const [states] = useState([
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
    "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
    "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
    "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
    "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Chandigarh", "Puducherry",
    "Ladakh", "Jammu and Kashmir", "Lakshadweep"
  ]);
  const [statusOptions] = useState(["Pending", "Closed", "Cancelled"]);
  const [products] = useState(["Transformer", "Generator", "Motor", "Solar"]);

  // Auto-generate enquiry ID
  useEffect(() => {
    const generateEnquiryID = async () => {
      const currentDate = formData.date ? new Date(formData.date) : new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const year = String(currentDate.getFullYear()).slice(-2);

      try {
        const response = await apiClient.get('/api/enquiries/count/current-month');
        const count = response.data.count || 0;
        const newEnquiryID = `VC${month}${year}${(count + 1).toString().padStart(2, "0")}`;
        setFormData(prev => ({ ...prev, enquiryID: newEnquiryID }));
      } catch (error) {
        const timestamp = Date.now().toString().slice(-4);
        const newEnquiryID = `VC${month}${year}${timestamp}`;
        setFormData(prev => ({ ...prev, enquiryID: newEnquiryID }));
      }
    };

    generateEnquiryID();
  }, [formData.date]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get('/api/enquiries/categories');
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Update subject based on form data
  useEffect(() => {
    const { enquiryID, quotationFor, city, state } = formData;
    if (enquiryID && quotationFor && city && state) {
      const subject = `${enquiryID}:- Request for quotation for ${quotationFor} at ${city}, ${state}`;
      setFormData(prev => ({ ...prev, description: subject }));
    }
  }, [formData.enquiryID, formData.quotationFor, formData.city, formData.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAttributeChange = (index, field, value) => {
    const updatedAttributes = [...formData.attributes];
    updatedAttributes[index][field] = value;
    setFormData(prev => ({ ...prev, attributes: updatedAttributes }));
  };

  const handleAddAttribute = () => {
    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, { name: "", value: "" }],
    }));
  };

  const handleRemoveAttribute = (index) => {
    if (formData.attributes.length > 1) {
      setFormData(prev => ({
        ...prev,
        attributes: prev.attributes.filter((_, i) => i !== index),
      }));
    }
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    const maxSize = 10 * 1024 * 1024;

    if (file) {
      if (!allowedTypes.includes(file.type)) {
        alert("Unsupported file type. Please upload a PDF, JPEG, or PNG.");
        e.target.value = "";
        return;
      }
      if (file.size > maxSize) {
        alert("File size exceeds the limit of 10 MB.");
        e.target.value = "";
        return;
      }
      setFormData(prev => ({ ...prev, attachment: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requiredFields = [
      { name: 'buyerName', label: 'Buyer Name' },
      { name: 'quotationFor', label: 'Product Name' },
      { name: 'productType', label: 'Product Type' },
      { name: 'phoneNumber', label: 'Phone Number' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' }
    ];
    
    const missingFields = requiredFields.filter(field => !formData[field.name] || formData[field.name].trim() === '');
    
    if (missingFields.length > 0) {
      const fieldLabels = missingFields.map(field => field.label).join(', ');
      alert(`Please fill in all required fields: ${fieldLabels}`);
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === "attachment" && formData[key] instanceof File) {
        formDataToSend.append(key, formData[key]);
      } else if (key === "attributes") {
        formDataToSend.append(key, JSON.stringify(formData[key]));
      } else {
        const value = formData[key];
        const isOptionalField = ['emailID', 'note', 'cat_Id'].includes(key);
        const finalValue = isOptionalField && (value === '' || value === null) ? null : value;
        formDataToSend.append(key, finalValue);
      }
    });

    try {
      await apiClient.post('/api/enquiries', formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      alert("Enquiry added successfully!");
      handleAddNewEnquiry(formData);
      setFormData({
        enquiryID: "",
        date: new Date().toISOString().split('T')[0],
        buyerName: "",
        description: "",
        quotationFor: "",
        productType: "",
        emailID: "",
        phoneNumber: "",
        city: "",
        state: "",
        status: "Pending",
        cat_Id: "",
        note: "",
        attributes: [{ name: "", value: "" }],
        attachment: null,
      });
    } catch (error) {
      console.error('Error adding enquiry:', error);
      alert("Error adding enquiry. Please try again.");
    }
  };

  const clearFormFields = () => {
    setFormData({
      enquiryID: "",
      date: new Date().toISOString().split('T')[0],
      buyerName: "",
      description: "",
      quotationFor: "",
      productType: "",
      emailID: "",
      phoneNumber: "",
      city: "",
      state: "",
      status: "Pending",
      cat_Id: "",
      note: "",
      attributes: [{ name: "", value: "" }],
      attachment: null,
    });
  };

  if (!showAddForm) return null;

  return (
    <div className="addLead-modal-overlay">
      <div className="addLead-modal-content">
        <div className="popup-header">
          <Header title="Add New Enquiry" />
          <Button onClick={closePopup} className="close-btn top-right" title="Close">×</Button>
        </div>
      
        <div className="popup-body">
          <div className="popup-grid">
            <div className="details-section">
              <h4>Enquiry Details</h4>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Enquiry ID:</label>
                    <input
                      type="text"
                      name="enquiryID"
                      value={formData.enquiryID}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label>Date:</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Product Name:</label>
                    <input
                      type="text"
                      name="quotationFor"
                      value={formData.quotationFor}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Buyer Name:</label>
                    <input
                      type="text"
                      name="buyerName"
                      value={formData.buyerName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number:</label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email ID:</label>
                    <input
                      type="email"
                      name="emailID"
                      value={formData.emailID}
                      onChange={handleChange}
                      placeholder="Email ID"
                    />
                  </div>

                  <div className="form-group">
                    <label>City:</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>State:</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((state, index) => (
                        <option key={index} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Categories:</label>
                    <select
                      name="cat_Id"
                      value={formData.cat_Id}
                      onChange={handleChange}
                    >
                      <option value="">Select Category</option>
                      {categories.map((category, index) => (
                        <option key={index} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Product Type:</label>
                    <select
                      name="productType"
                      value={formData.productType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Product</option>
                      {products.map((productType, index) => (
                        <option key={index} value={productType}>{productType}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status:</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a Status</option>
                      {statusOptions.map((status, index) => (
                        <option key={index} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Attributes:</label>
                    {formData.attributes.map((attribute, index) => (
                      <div key={index} className="attribute-row">
                        <input
                          type="text"
                          placeholder="Attribute Name"
                          value={attribute.name}
                          onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Attribute Value"
                          value={attribute.value}
                          onChange={(e) => handleAttributeChange(index, "value", e.target.value)}
                        />
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttribute(index)}
                            className="remove-attribute-btn"
                          >
                            <FiTrash size={16} color="white" />
                          </button>
                        )}
                        {index === formData.attributes.length - 1 && (
                          <Button
                            title="add"
                            type="button"
                            onClick={handleAddAttribute}
                            className="add-attribute-btn"
                          >
                            <FiPlus size={16} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="form-group">
                    <label>Note:</label>
                    <textarea
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      rows="3"
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label>Subject:</label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label>Upload Document:</label>
                    <input
                      type="file"
                      onChange={handleDocumentChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </div>

                  <div className="modal-buttons">
                    <Button
                      title="submit"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Adding...' : 'Add Enquiry'}
                    </Button>
                    <Button
                      className="cancel"
                      title="cancel"
                      type="button"
                      onClick={handleToggleAddForm}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEnquiryForm;
